import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Enhanced error logging for PWA issues
    console.error('PWA Error Boundary:', error, errorInfo);
    
    // Check if this is a PWA-specific error
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isServiceWorkerError = error?.message?.includes('service worker') || 
                                error?.stack?.includes('sw.js') ||
                                error?.name === 'SecurityError';
    
    console.error('Error Context:', {
      isPWA,
      isServiceWorkerError,
      userAgent: navigator.userAgent,
      error: error.toString(),
      stack: error.stack,
      serviceWorkerState: 'serviceWorker' in navigator ? 
        navigator.serviceWorker.controller?.state : 'not supported'
    });
    
    // If it's a service worker error, try to handle it gracefully
    if (isServiceWorkerError && isPWA) {
      console.warn('Service Worker error detected in PWA mode, attempting recovery...');
      
      // Try to unregister problematic service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister().catch(err => 
              console.warn('Failed to unregister service worker:', err)
            );
          });
        }).catch(err => {
          console.warn('Failed to get service worker registrations:', err);
        });
      }
    }
  }

  handleReload = () => {
    const isServiceWorkerError = this.state.error?.message?.includes('service worker') || 
                                this.state.error?.stack?.includes('sw.js') ||
                                this.state.error?.name === 'SecurityError';
    
    if (isServiceWorkerError && 'serviceWorker' in navigator) {
      // Force hard reload to bypass service worker
      window.location.reload(true);
    } else {
      window.location.reload();
    }
  };
  
  handleClearCache = async () => {
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      
      // Clear localStorage
      localStorage.clear();
      sessionStorage.clear();
      
      // Hard reload
      window.location.reload(true);
    } catch (error) {
      console.error('Failed to clear cache:', error);
      window.location.reload(true);
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              Something went wrong
            </h1>
            
            <p className="text-slate-600 mb-4">
              {this.state.error?.message?.includes('service worker') || 
               this.state.error?.stack?.includes('sw.js') 
                ? 'The app encountered a service worker error. Try clearing the cache if reloading doesn\'t work.' 
                : window.matchMedia('(display-mode: standalone)').matches 
                ? 'The app encountered an error in PWA mode.' 
                : 'The app encountered an error.'
              }
            </p>
            
            <div className="space-y-2">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors w-full justify-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload App
              </button>
              
              {/* Show cache clearing option for PWA errors */}
              {(this.state.error?.message?.includes('service worker') || 
                this.state.error?.stack?.includes('sw.js') ||
                window.matchMedia('(display-mode: standalone)').matches) && (
                <button
                  onClick={this.handleClearCache}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors w-full justify-center text-sm"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Clear Cache & Reload
                </button>
              )}
            </div>
            
            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-stone-500 mb-2">
                  Error Details
                </summary>
                <div className="bg-stone-100 p-3 rounded text-xs overflow-auto">
                  <div className="font-medium text-red-600 mb-2">
                    {this.state.error.toString()}
                  </div>
                  <div className="text-slate-700 whitespace-pre-wrap">
                    {this.state.errorInfo?.componentStack}
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;